// Contracts: families of fixed, numbered versions.
//
// A family is the thing people talk about ("Group A Master Sublicence"); a
// version is one fixed text of it. Versions go draft -> published -> retired
// and never back, and the database refuses any change to a version once it
// has left draft. Nothing that sends an agreement picks a version: the server
// sends the family's published one. See HHNodeServer contractService.

import type { CultivarRightsState } from './cultivar';

export type ContractTier =
  | 'nursery_master_sublicence'
  | 'nursery_propagation'
  | 'orchard_planting'
  | 'end_customer_non_prop'
  | 'end_customer_royalty'
  | 'marketing_approval';

export const CONTRACT_TIER_LABELS: Record<ContractTier, string> = {
  nursery_master_sublicence: 'Master nursery sublicence',
  nursery_propagation: 'Nursery propagation licence',
  orchard_planting: 'Orchard planting licence',
  end_customer_non_prop: 'End customer — no propagation',
  end_customer_royalty: 'End customer — royalty',
  marketing_approval: 'Marketing approval',
};

export const CONTRACT_TIERS = Object.keys(CONTRACT_TIER_LABELS) as ContractTier[];

export type ContractVersionStatus = 'draft' | 'published' | 'retired';

/** One row of the contracts list. */
export interface ContractFamily {
  id: number;
  organisation_id: number;
  license_tier: ContractTier;
  cultivar_id: number | null;
  cultivar_name: string | null;
  cultivar_trade_name: string | null;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  current_version_id: number | null;
  current_version: number | null;
  current_published_at: string | null;
  current_content_hash: string | null;
  draft_version_id: number | null;
  draft_version: number | null;
  draft_updated_at: string | null;
  version_count: number;
  pending_count: number;
  signed_count: number;
}

export interface ContractVersion {
  id: number;
  version: number;
  status: ContractVersionStatus;
  name: string;
  body_richtext: string;
  merge_fields: string[];
  /** The merge fields the text actually contains, in order of appearance. */
  tokens: string[];
  /** SHA-256 of the text; null only on a draft. */
  content_hash: string | null;
  /** SHA-256 of the text as it stands, drafts included. */
  text_hash: string;
  change_summary: string | null;
  created_at: string;
  updated_at: string;
  created_by_name: string | null;
  published_at: string | null;
  published_by_name: string | null;
  retired_at: string | null;
  retired_by_name: string | null;
  agreements_sent: number;
  order_documents_sent: number;
}

export interface ContractFamilyDetail extends Omit<ContractFamily,
  'current_version_id' | 'current_version' | 'current_published_at' | 'current_content_hash' |
  'draft_version_id' | 'draft_version' | 'draft_updated_at' | 'version_count' | 'pending_count' | 'signed_count'> {
  versions: ContractVersion[];
  current: ContractVersion | null;
  draft: ContractVersion | null;
  used_by_programs: { program_id: number; program_name: string; role: string; required: boolean }[];
  used_by_groups: { id: number; code: string; name: string; status: string; as_master: boolean; as_schedule: boolean }[];
  pending_agreements: {
    id: number;
    template_version: number | null;
    created_at: string;
    licensee_org_name: string | null;
    /** False for an agreement issued before versioning: it is withdrawn, not reissued. */
    can_reissue: boolean;
  }[];
}

export interface ContractPublishResult {
  family: ContractFamilyDetail;
  reissue: {
    reissued: { id: number; replacement_id: number; licensee_org_name: string | null }[];
    withdrawn: { id: number; licensee_org_name: string | null; reason: string }[];
    error?: string;
  };
  redispatch: {
    redispatched: { order_id: number }[];
    failed: { order_id: number; role: string; reason: string }[];
    error?: string;
  };
}

export interface ContractDiffLine {
  op: 'same' | 'add' | 'remove';
  text: string;
}

export interface ContractDiff {
  from: { id: number; version: number; status: ContractVersionStatus };
  to: { id: number; version: number; status: ContractVersionStatus };
  stats: { added: number; removed: number };
  lines: ContractDiffLine[];
}

export interface ContractEvent {
  id: number;
  action: string;
  summary: string | null;
  details: Record<string, unknown>;
  content_hash: string | null;
  actor_name: string | null;
  ip_address?: string | null;
  user_agent?: string | null;
  created_at: string;
  family_id?: number | null;
  template_id?: number | null;
  license_id?: number | null;
  order_id?: number | null;
  program_id?: number | null;
  sublicence_group_id?: number | null;
}

export type ContractEventKind = 'family' | 'license' | 'program' | 'group' | 'order';

/** What an issue would send, from POST /cultivar/licenses/preview. */
export interface LicensePreview {
  family: { id: number; name: string; license_tier: ContractTier };
  version: {
    id: number;
    version: number;
    published_at: string | null;
    content_hash: string | null;
    change_summary: string | null;
  };
  merged_terms: string;
  /** Hand back on issue as expected_content_hash. */
  content_hash: string;
  /** Fields the sender types. */
  custom_fields: string[];
  /** Custom fields left empty; they print as a line to fill by hand. */
  blank_fields: string[];
  /** Fields the server fills from the licence itself. */
  derived_fields: string[];
  values: Record<string, string>;
  /** Whether FactreeTech has checked the issuer's rights in the cultivar; what the licensee will see. */
  cultivar_rights_state?: CultivarRightsState | null;
}

/**
 * Fields the server fills in itself when an agreement is sent — party names,
 * cultivar and PBR details, dates, quantities. Mirrors licenseService
 * _deriveMergeValues; anything else in a text is typed by the sender.
 */
export const DERIVED_MERGE_FIELDS: { key: string; label: string }[] = [
  { key: 'licensor_org', label: 'Licensor' },
  { key: 'licensee_org', label: 'Licensee' },
  { key: 'supplying_nursery', label: 'Supplying nursery' },
  { key: 'cultivar', label: 'Cultivar (trade name if any)' },
  { key: 'cultivar_name', label: 'Cultivar name' },
  { key: 'cultivar_trade_name', label: 'Cultivar trade name' },
  { key: 'cultivar_protection_status', label: 'Protection status' },
  { key: 'cultivar_pbr_number', label: 'PBR number' },
  { key: 'cultivar_pbr_grant_date', label: 'PBR grant date' },
  { key: 'cultivar_pbr_expiry_date', label: 'PBR expiry date' },
  { key: 'rootstock_name', label: 'Rootstock' },
  { key: 'rootstock_protection_status', label: 'Rootstock protection' },
  { key: 'rootstock_pbr_number', label: 'Rootstock PBR number' },
  { key: 'rootstock_pbr_expiry_date', label: 'Rootstock PBR expiry' },
  { key: 'rootstock_rights_holder', label: 'Rootstock rights holder' },
  { key: 'sublicence_group_code', label: 'Sublicence group code' },
  { key: 'sublicence_group_name', label: 'Sublicence group name' },
  { key: 'region', label: 'Region' },
  { key: 'start_date', label: 'Start date' },
  { key: 'end_date', label: 'End date' },
  { key: 'max_trees', label: 'Trees' },
  { key: 'tolerance_pct', label: 'Tolerance %' },
];

/** Plain-language line for an event action. */
export const CONTRACT_EVENT_LABELS: Record<string, string> = {
  contract_created: 'Contract created',
  contract_renamed: 'Details changed',
  contract_withdrawn: 'Contract withdrawn',
  draft_started: 'Draft started',
  draft_edited: 'Draft edited',
  draft_discarded: 'Draft discarded',
  version_published: 'Version published',
  version_recorded: 'Recorded at versioning',
  agreement_issued: 'Agreement sent',
  agreement_reissued: 'Agreement reissued',
  agreement_superseded: 'Agreement replaced',
  agreement_withdrawn: 'Agreement withdrawn',
  agreement_signed: 'Signed',
  agreement_declined: 'Declined',
  agreement_suspended: 'Suspended',
  agreement_terminated: 'Terminated',
  program_role_assigned: 'Programme contract set',
  program_role_removed: 'Programme contract removed',
  order_document_sent: 'Sent for signature',
  order_document_voided: 'Order contract cancelled',
  order_document_signed: 'Signed in Adobe Sign',
  group_created: 'Group created',
  group_changed: 'Group changed',
  sublicensing_changed: 'Sublicensing changed',
};

/** The {{tokens}} in a text, in order of first appearance. */
export function contractTokens(body: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const m of body.matchAll(/\{\{\s*([a-zA-Z0-9_.]+)\s*\}\}/g)) {
    if (!seen.has(m[1])) { seen.add(m[1]); out.push(m[1]); }
  }
  return out;
}

/** A fingerprint short enough to read aloud; the full value is always one click away. */
export function shortHash(hash: string | null | undefined): string {
  return hash ? `${hash.slice(0, 8)}…${hash.slice(-4)}` : '—';
}
